import { useCallback, useMemo, useRef, useState } from 'react';

import { AccessorKeyColumnDef } from '@tanstack/table-core';
import { HideSubListItem } from './HideSubListItem';
import { Item } from '../../types/item.types';
import { ListSubheader } from '@mui/material';
import { SubItem } from '../../types/sub-item.types';
import { useColumnOrderState } from '../../state/column-order.state';
import { applyOrder } from '../../../../shared/utils/order';
import { useHiddenColumnState } from '../../state/column-hiding.state';

type TableSection = 'main' | 'expanded';

interface HideListProps {
   title: string;
   columns: (AccessorKeyColumnDef<SubItem> | AccessorKeyColumnDef<Item>)[];
   section: TableSection;
}

const COLUMN_EXCLUDE = ['action'];

export const HideSubList = ({ columns, section, title }: HideListProps) => {
   const order = useColumnOrderState((s) => s.orders[section]);
   const reorder = useColumnOrderState((s) => s.reorder);
   const columnsMoveable = useColumnOrderState((s) => s.columnsMoveable);
   const dragIndexRef = useRef<number | null>(null);
   const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
   const setHiddenColumns = useHiddenColumnState((s) => s.setHiddenColumns);

   const filteredColumns = columns.filter((col) => !COLUMN_EXCLUDE.includes(col.accessorKey));
   const orderedColumns = useMemo(() => applyOrder(filteredColumns, order, (c) => c.accessorKey), [filteredColumns, order]);

   const handleDragStart = useCallback((index: number) => {
      dragIndexRef.current = index;
   }, []);

   const handleDragOver = useCallback((index: number) => {
      setDragOverIndex(index);
   }, []);

   const handleDrop = useCallback(
      (toIndex: number) => {
         const fromIndex = dragIndexRef.current;
         if (fromIndex !== null && fromIndex !== toIndex) {
            const movedColumn = orderedColumns[fromIndex]?.accessorKey;
            const targetColumn = orderedColumns[toIndex]?.accessorKey;

            if (movedColumn && targetColumn) {
               reorder(section, movedColumn, targetColumn);
               setHiddenColumns((prev) => ({ ...prev, [movedColumn]: true }));
            }
         }
         dragIndexRef.current = null;
         setDragOverIndex(null);
      },
      [orderedColumns, reorder, section, setHiddenColumns]
   );

   const handleDragEnd = useCallback(() => {
      dragIndexRef.current = null;
      setDragOverIndex(null);
   }, []);

   return (
      <li key={'section-' + title}>
         <ul>
            <ListSubheader>{title}</ListSubheader>
            {orderedColumns.map((column, index) => (
               <HideSubListItem
                  key={column.accessorKey}
                  column={column}
                  index={index}
                  isDragOver={dragOverIndex === index}
                  columnsMoveable={columnsMoveable}
                  onDragStart={handleDragStart}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                  onDragEnd={handleDragEnd}
               />
            ))}
         </ul>
      </li>
   );
};
